import { useState } from "react";
import { PlayAudioButton } from "@/components/PlayAudioButton";
import { Button } from "@/components/ui/button";
import { useRomanianAudio } from "@/lib/audio";
import { useT } from "@/lib/i18n";
import { type ScenarioConstruction, useGradeChunk } from "@/lib/scenarios";

type ChunkStepProps = {
  scenarioSlug: string;
  construction: ScenarioConstruction;
  onComplete: () => void;
};

/**
 * CHUNK step — recognition exposure of a memorable phrase. Show the
 * Romanian construction with an audio button; tap (or press the
 * reveal CTA) to surface the Russian translation. Tap "Дальше" to
 * advance.
 *
 * v2 replaces the old LINE step, which asked the learner to type
 * RU → RO from memory. That was a production drill — wrong for
 * A0/A1 study sessions where the learner is still being exposed to
 * the phrase for the first time. Real production happens at the
 * checkpoint. Sessions are now strictly study + light practice.
 *
 * On reveal we still grade the chunk SRS forward once (treating the
 * reveal as a successful "I recognised this" beat). That keeps the
 * gentle progression toward MATURE that ChunkProgress was designed
 * for, even though we no longer prompt for production input.
 */
export function ChunkStep({ scenarioSlug, construction, onComplete }: ChunkStepProps) {
  const t = useT();
  const grade = useGradeChunk(scenarioSlug);
  const [revealed, setRevealed] = useState(false);

  // Autoplay the Romanian phrase when the card mounts. The hook fires
  // on string-identity change of its arg, so a fresh CHUNK step kicks
  // off a fresh playback and the manual replay button picks up the
  // same phrase without us threading state.
  const { play } = useRomanianAudio(construction.ro);

  const onReveal = () => {
    if (revealed) return;
    setRevealed(true);
    // One-shot SRS bump per session-exposure. We ignore the response;
    // the network call is fire-and-forget — if it fails the user
    // doesn't lose any UX, the chunk just doesn't advance that beat.
    grade.mutate({ chunkId: construction.id, correct: true });
  };

  return (
    <div className="flex flex-1 flex-col gap-4">
      <button
        type="button"
        onClick={onReveal}
        disabled={revealed}
        className="flashcard flex flex-1 cursor-pointer flex-col items-stretch gap-3 text-left"
      >
        <div className="font-mono text-[11px] font-semibold uppercase tracking-wider text-[color:var(--scen-fg)]">
          {t("skills.detail_constructions")}
        </div>

        <div className="mt-1 flex items-center justify-center gap-3 py-2">
          <span className="text-center text-[22px] font-semibold leading-[1.35] text-text">{construction.ro}</span>
          <PlayAudioButton play={play} className="size-8" />
        </div>

        {revealed ? (
          <div className="mt-1 border-t border-dashed border-border pt-3 text-center text-[16px] leading-[1.4] text-text-2">
            {construction.ru}
          </div>
        ) : (
          <div className="mt-1 flex flex-1 items-center justify-center pt-1 text-[13px] text-text-3">
            {t("study.tap_or_space")}
          </div>
        )}
      </button>

      <Button variant="scenario" size="block" disabled={!revealed} onClick={onComplete}>
        {t("skills.session_continue")}
      </Button>
    </div>
  );
}
